import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import authService from './services/authService';
import { startRealtime } from './realtime';
import { startMobileUpdateCheck } from './mobileUpdate';
import { AppearanceProvider } from './context/AppearanceContext';
import { CompanyProvider } from './context/CompanyContext';

// Layouts
import ResponsiveLayout from './layouts/ResponsiveLayout';

// Responsive route switch + mobile screens
import Adaptive from './components/Adaptive';
import IdentityGateHost from './components/IdentityGateHost';
import { useManagerSession, ReservedGate } from './components/ManagerMode';
import MobileHome from './mobile/pages/MobileHome';
import MobileProductList from './mobile/pages/MobileProductList';
import MobileCustomerList from './mobile/pages/MobileCustomerList';
import MobileSupplierList from './mobile/pages/MobileSupplierList';
import MobileServicesList from './mobile/pages/MobileServicesList';
import MobileFacturationList from './mobile/pages/MobileFacturationList';
import MobileReservationsList from './mobile/pages/MobileReservationsList';
import MobileSalesStatistics from './mobile/pages/MobileSalesStatistics';
import MobileGeneralStatistics from './mobile/pages/MobileGeneralStatistics';
import MobileSellerHistory from './mobile/pages/MobileSellerHistory';
import MobileServiceHistory from './mobile/pages/MobileServiceHistory';
import MobileCategoryList from './mobile/pages/MobileCategoryList';
import MobileLaboratoireList from './mobile/pages/MobileLaboratoireList';
import MobileUserList from './mobile/pages/MobileUserList';

// Pages
import Login from './pages/Login';
import PostePage from './pages/poste/PostePage';
import Dashboard from './pages/Dashboard';
import SalesStatistics from './pages/statistics/SalesStatistics';
import GeneralStatistics from './pages/statistics/GeneralStatistics';
import ActionHistory from './pages/statistics/ActionHistory';

// Sales Pages
import SaleReceipt from './pages/sales/SaleReceipt';
import SellerSalesPage from './pages/sales/SellerSalesPage';
import SellerHistorySales from './pages/sales/SellerHistorySales';
import ServiceHistory from './pages/sales/ServiceHistory';
import ReservationsList from './pages/reservations/ReservationsList';

// Devis Page
import DevisPage from './pages/devis/DevisPage';

// Devis Pages

// Product Pages
import ProductList from './pages/products/ProductList';
import AddProduct from './pages/products/AddProduct';
import AddPromo from './pages/products/AddPromo';

// Customer Pages
import CustomerList from './pages/customers/CustomerList';
import AddCustomer from './pages/customers/AddCustomer';
import ClientCategoryManagement from './pages/customers/ClientCategoryManagement';

// Category Pages
import CategoryList from './pages/categories/CategoryList';

// Supplier Pages
import SupplierList from './pages/suppliers/SupplierList';
import SupplierDetail from './pages/suppliers/SupplierDetail';
import AddSupplier from './pages/suppliers/AddSupplier';
import LaboratoireList from './pages/laboratoires/LaboratoireList';

// Facturation Pages
import ListFacturation from './pages/facturation/ListFacturation';
import AddFacturation from './pages/facturation/AddFacturation';
import FactureDetails from './pages/facturation/FactureDetails';
import ACommander from './pages/commandes/ACommander';
import CommandesList from './pages/commandes/CommandesList';
import CommandeDetail from './pages/commandes/CommandeDetail';

// Service Management Pages
import ServicesList from './pages/services/ServicesList';
import ServiceForm from './pages/services/ServiceForm';

// User Management Pages
import UserList from './pages/UserManagement/UserList';
import UserForm from './pages/UserManagement/UserForm';

// Settings (Parametres / Welcome page)
import SettingsPage from './pages/settings/SettingsPage';

// Placeholder component for other not yet implemented pages

// Authentication guard
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = authService.isAuthenticated();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Role-based guards
const AdminRoute = ({ children }) => {
  const isAdmin = authService.isAdmin();
  
  if (!isAdmin && authService.isCaisse()) return <ReservedGate>{children}</ReservedGate>;
  if (!isAdmin) {
    // Redirect to home if logged in but not admin
    return authService.isAuthenticated() ? <Navigate to="/" replace /> : <Navigate to="/login" replace />;
  }
  
  return children;
};

// Comptes et personnes : compte PROPRIÉTAIRE seulement (le manager a tout le reste).
const OwnerRoute = ({ children }) => {
  if (!authService.isOwner()) {
    return authService.isAuthenticated() ? <Navigate to="/" replace /> : <Navigate to="/login" replace />;
  }
  return children;
};

const ManagerRoute = ({ children }) => {
  const isManagerOrAdmin = authService.isManagerOrAdmin();

  if (!isManagerOrAdmin && authService.isCaisse()) return <ReservedGate>{children}</ReservedGate>;
  if (!isManagerOrAdmin) {
    // Redirect to home if logged in but not manager/admin
    return authService.isAuthenticated() ? <Navigate to="/" replace /> : <Navigate to="/login" replace />;
  }

  return children;
};

// Facturation / suppliers / laboratoires viewing: admin, or a seller explicitly
// granted facturation access. Sellers reach the LIST/DETAIL (financials hidden in
// the pages themselves); creating/editing factures stays ManagerRoute.
const FacturationRoute = ({ children }) => {
  if (!authService.hasFacturationAccess()) {
    if (authService.isCaisse()) return <ReservedGate>{children}</ReservedGate>;
    return authService.isAuthenticated() ? <Navigate to="/" replace /> : <Navigate to="/login" replace />;
  }
  return children;
};

function App() {
  // Caisse en mode manager : les routes réservées s'ouvrent / se referment.
  useManagerSession();
  // Live change notifications from the server (Redis stream): pages refetch,
  // product cache refreshes. No-op on the desktop app (its engine listens).
  useEffect(() => { startRealtime(); startMobileUpdateCheck(); }, []);
  return (
    <AppearanceProvider>
    <CompanyProvider>
    {/* « Qui agit ? » — fenêtre du code commune à tous les écrans */}
    <IdentityGateHost />
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        
        {/* Protected routes */}
        <Route path="/" element={
          <ProtectedRoute>
            <ResponsiveLayout />
          </ProtectedRoute>
        }>
          {/* Dashboard — all authenticated staff. Financial cards (Ventes/jour,
              Achats/jour) are hidden for sellers inside the Dashboard itself. */}
          <Route index element={
            <Adaptive desktop={Dashboard} mobile={MobileHome} />
          } />

          {/* Statistiques détaillées (depuis les courbes du tableau de bord) - Admin only */}
          <Route path="statistiques/ventes" element={
            <AdminRoute>
              <Adaptive desktop={SalesStatistics} mobile={MobileSalesStatistics} />
            </AdminRoute>
          } />
          <Route path="statistiques/generale" element={
            <AdminRoute>
              <Adaptive desktop={GeneralStatistics} mobile={MobileGeneralStatistics} />
            </AdminRoute>
          } />
          <Route path="statistiques/historique-actions" element={
            <AdminRoute>
              <ActionHistory />
            </AdminRoute>
          } />

          {/* Settings / Parametres (Welcome page) - Admin only */}
          <Route path="settings" element={
            <AdminRoute>
              <SettingsPage />
            </AdminRoute>
          } />

          {/* Desktop app only: synchronisation with the VPS + peripherals of this PC */}
          <Route path="poste" element={<PostePage />} />

          {/* Sales routes */}
          <Route path="vente/:id/receipt" element={<SaleReceipt />} />
          <Route path="seller-sales" element={<SellerSalesPage />} />
          <Route path="seller-history" element={<Adaptive desktop={SellerHistorySales} mobile={MobileSellerHistory} />} />
          <Route path="service-history" element={<Adaptive desktop={ServiceHistory} mobile={MobileServiceHistory} />} />
          <Route path="reservations" element={<Adaptive desktop={ReservationsList} mobile={MobileReservationsList} />} />

          {/* Devis — feuille libre, première entrée du menu ⚙ (admin) */}
          <Route path="devis" element={
            <AdminRoute>
              <DevisPage />
            </AdminRoute>
          } />
          
          {/* Product routes — la Liste des produits s'ouvre SANS code sur une caisse
              (owner 2026-10-03 ; la porte « code d'un manager » du 2026-09-30 est retirée) */}
          <Route path="products" element={<Adaptive desktop={ProductList} mobile={MobileProductList} />} />
          <Route path="products/promos" element={
            <ManagerRoute>
              <AddPromo />
            </ManagerRoute>
          } />
          {/* Sellers may add a product (but not edit existing ones). */}
          <Route path="products/add" element={<AddProduct />} />
          <Route path="products/:id/edit" element={
            <ManagerRoute>
              <AddProduct />
            </ManagerRoute>
          } />
          
          {/* Categories routes */}
          <Route path="categories" element={
            <ManagerRoute>
              <Adaptive desktop={CategoryList} mobile={MobileCategoryList} />
            </ManagerRoute>
          } />

          {/* Customer routes */}
          <Route path="clients" element={<Adaptive desktop={CustomerList} mobile={MobileCustomerList} />} />
          <Route path="clients/add" element={<AddCustomer />} />
          <Route path="clients/categories" element={<ClientCategoryManagement />} />
          <Route path="clients/:id/edit" element={
            <ManagerRoute>
              <AddCustomer />
            </ManagerRoute>
          } />
          
          {/* Supplier routes */}
          <Route path="suppliers" element={<Adaptive desktop={SupplierList} mobile={MobileSupplierList} />} />
          <Route path="suppliers/add" element={
            <ManagerRoute>
              <AddSupplier />
            </ManagerRoute>
          } />
          <Route path="suppliers/:id" element={<SupplierDetail />} />
          <Route path="laboratoires" element={<Adaptive desktop={LaboratoireList} mobile={MobileLaboratoireList} />} />

          {/* Facturation routes */}
          <Route path="facturation/list" element={
            <FacturationRoute>
              <Adaptive desktop={ListFacturation} mobile={MobileFacturationList} />
            </FacturationRoute>
          } />
          {/* Owner 2026-09-21 : le compte vendeur a TOUJOURS accès à « Ajouter
              facture » (la personne donne son code à l'enregistrement). La
              liste / le détail restent sous « Accès facturation », modifier
              une facture reste réservé (ManagerRoute). */}
          <Route path="facturation/add" element={<AddFacturation />} />
          <Route path="facturation/edit/:id" element={
            <ManagerRoute>
              <AddFacturation />
            </ManagerRoute>
          } />
          <Route path="facturation/detail/:id" element={
            <FacturationRoute>
              <FactureDetails />
            </FacturationRoute>
          } />

          {/* Bons de commande fournisseur (réapprovisionnement) */}
          {/* « Nouvelle commande » is open to every seller account (owner request);
              the brouillons/envoyés/reçus lists stay facturation-gated. */}
          <Route path="commandes/a-commander" element={<ACommander />} />
          <Route path="commandes" element={<Navigate to="/commandes/brouillons" replace />} />
          <Route path="commandes/brouillons" element={
            <FacturationRoute>
              <CommandesList status="BROUILLON" />
            </FacturationRoute>
          } />
          <Route path="commandes/envoyes" element={
            <FacturationRoute>
              <CommandesList status="ENVOYE" />
            </FacturationRoute>
          } />
          <Route path="commandes/recus" element={
            <FacturationRoute>
              <CommandesList status="RECU" />
            </FacturationRoute>
          } />
          {/* Open too: ACommander redirects here right after creating a commande. */}
          <Route path="commandes/:id" element={<CommandeDetail />} />
          
          {/* Service Management routes */}
          {/* Both seller and admin can view services list */}
          <Route path="services" element={<Adaptive desktop={ServicesList} mobile={MobileServicesList} />} />
          
          {/* Only admin can add/edit services */}
          <Route path="services/add" element={
            <AdminRoute>
              <ServiceForm />
            </AdminRoute>
          } />
          <Route path="services/edit/:id" element={
            <AdminRoute>
              <ServiceForm />
            </AdminRoute>
          } />

          {/* Comptes — compte propriétaire seulement */}
          <Route path="users" element={
            <OwnerRoute>
              <Adaptive desktop={UserList} mobile={MobileUserList} />
            </OwnerRoute>
          } />
          <Route path="users/add" element={
            <OwnerRoute>
              <UserForm />
            </OwnerRoute>
          } />
          <Route path="users/edit/:userId" element={
            <OwnerRoute>
              <UserForm />
            </OwnerRoute>
          } />
        </Route>
        
        {/* Redirect any other route to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
    </CompanyProvider>
    </AppearanceProvider>
  );
}

export default App;
